import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  TextInput,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Image } from 'expo-image';
import { resolveSignatureUrlForStudentName } from '../utils/studentSignatures';
import { formatClassMoney, formatSignedBalance, EXTRA_CONTRIBUTION_REASONS, formatExtraContributionLabel } from '../utils/classBookApi';

const PAPER = '#FAF6EE';
const INK = '#2C2416';
const RULE = 'rgba(44, 36, 22, 0.14)';
const COL_NUM_WIDTH = 22;
const COL_FEE_WIDTH = 54;
const COL_PAID_WIDTH = 108;
const COL_BALANCE_WIDTH = 58;
const COL_MONEY_GAP = 4;

function buildDiaryMeta(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  return {
    weekday: d.toLocaleDateString(undefined, { weekday: 'long' }),
    dayNumber: d.getDate(),
    monthYear: d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
  };
}

function normalizeLines(attendanceLines) {
  if (!Array.isArray(attendanceLines)) return [];
  return attendanceLines
    .filter((line) => line?.student_name)
    .map((line) => {
      const feeAmount = Number(line.fee_amount ?? 0);
      const allocated = Number(line.amount_allocated ?? 0);
      const balance =
        line.year_balance != null
          ? Number(line.year_balance)
          : Math.round((feeAmount - allocated) * 100) / 100;
      return {
        id: line.id,
        student_name: String(line.student_name).trim(),
        fee_exempt: line.fee_exempt === true,
        fee_amount: feeAmount,
        fee_currency: line.fee_currency || 'EUR',
        amount_allocated: allocated,
        balance,
        extra_contributions: Array.isArray(line.extra_contributions) ? line.extra_contributions : [],
      };
    });
}

function parseDraftAmount(value) {
  const n = Number(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export default function ClassBookDiaryPage({
  date,
  attendanceLines = [],
  attendanceCount = 0,
  feeRate = null,
  noClass = false,
  signatureUrlByKey = {},
  onSavePaymentToday,
  onSaveExtraContribution,
  savingLinePayment = false,
  savingLineExtra = false,
}) {
  const meta = useMemo(() => buildDiaryMeta(date), [date]);
  const currency = feeRate?.currency || attendanceLines[0]?.fee_currency || 'EUR';
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [draftPaid, setDraftPaid] = useState('');
  const [showExtraEditor, setShowExtraEditor] = useState(false);
  const [draftExtraPaid, setDraftExtraPaid] = useState('');
  const [draftExtraReason, setDraftExtraReason] = useState(EXTRA_CONTRIBUTION_REASONS[0]);
  const [showReasonDropdown, setShowReasonDropdown] = useState(false);
  const [paymentEditorError, setPaymentEditorError] = useState('');
  const [extraEditorError, setExtraEditorError] = useState('');
  const editorBusy = savingLinePayment || savingLineExtra;

  const lines = useMemo(() => {
    const rows = normalizeLines(attendanceLines);
    const minLines = Math.max(10, rows.length);
    while (rows.length < minLines) {
      rows.push({
        id: `empty-${rows.length}`,
        student_name: '',
        fee_exempt: false,
        fee_amount: 0,
        fee_currency: currency,
        amount_allocated: 0,
        balance: 0,
        extra_contributions: [],
      });
    }
    return rows;
  }, [attendanceLines, currency]);

  const feeLabel =
    feeRate?.amount != null
      ? `Class fee: ${formatClassMoney(feeRate.amount, feeRate.currency || currency)}`
      : null;

  const resetPaymentEditor = () => {
    setSelectedLineId(null);
    setDraftPaid('');
    setShowExtraEditor(false);
    setDraftExtraPaid('');
    setDraftExtraReason(EXTRA_CONTRIBUTION_REASONS[0]);
    setShowReasonDropdown(false);
    setPaymentEditorError('');
    setExtraEditorError('');
  };

  const toggleReasonDropdown = () => {
    if (editorBusy) return;
    setShowReasonDropdown((open) => !open);
  };

  const togglePaymentEditor = (line) => {
    if (!line.student_name || line.fee_exempt || editorBusy) return;
    if (selectedLineId === line.id) {
      resetPaymentEditor();
      return;
    }
    setSelectedLineId(line.id);
    setDraftPaid('');
    setShowExtraEditor(false);
    setDraftExtraPaid('');
    setDraftExtraReason(EXTRA_CONTRIBUTION_REASONS[0]);
    setShowReasonDropdown(false);
    setPaymentEditorError('');
    setExtraEditorError('');
  };

  const toggleExtraEditor = () => {
    if (editorBusy) return;
    setShowExtraEditor((open) => !open);
    setShowReasonDropdown(false);
    setExtraEditorError('');
    if (!showExtraEditor) {
      setDraftExtraPaid('');
      setDraftExtraReason(EXTRA_CONTRIBUTION_REASONS[0]);
    }
  };

  const adjustDraftPaid = (delta) => {
    const next = Math.max(0, parseDraftAmount(draftPaid) + delta);
    setDraftPaid(String(next));
  };

  const adjustDraftExtraPaid = (delta) => {
    const next = Math.max(0, parseDraftAmount(draftExtraPaid) + delta);
    setDraftExtraPaid(String(next));
  };

  const handleSavePayment = async (line) => {
    if (!onSavePaymentToday || line.fee_exempt || editorBusy) return;
    const amount = parseDraftAmount(draftPaid);
    if (amount <= 0) {
      setPaymentEditorError('Enter an amount paid today');
      return;
    }
    setPaymentEditorError('');
    try {
      await onSavePaymentToday(line.id, line.student_name, amount, line.fee_currency || currency);
      resetPaymentEditor();
    } catch (error) {
      setPaymentEditorError(error?.message || 'Failed to save payment');
    }
  };

  const handleSaveExtra = async (line) => {
    if (!onSaveExtraContribution || line.fee_exempt || editorBusy) return;
    const amount = parseDraftAmount(draftExtraPaid);
    if (amount <= 0) {
      setExtraEditorError('Enter an extra amount');
      return;
    }
    if (!draftExtraReason) {
      setExtraEditorError('Choose a reason');
      return;
    }
    setExtraEditorError('');
    try {
      await onSaveExtraContribution(
        line.id,
        line.student_name,
        amount,
        line.fee_currency || currency,
        draftExtraReason
      );
      resetPaymentEditor();
    } catch (error) {
      setExtraEditorError(error?.message || 'Failed to save extra contribution');
    }
  };

  return (
    <View
      style={styles.shell}
      {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryPage: 'true' } } : {})}
    >
      <View style={styles.page}>
        {noClass ? (
          <View
            style={styles.noClassStampWrap}
            pointerEvents="none"
            {...(Platform.OS === 'web' ? { dataSet: { classBookNoClassStamp: 'true' } } : {})}
          >
            <View style={styles.noClassStamp}>
              <Text style={styles.noClassStampText}>No Class</Text>
            </View>
          </View>
        ) : null}

        <View style={styles.headerRow}>
          <View style={styles.dateBlock}>
            <Text style={styles.weekday}>{meta.weekday}</Text>
            <Text style={styles.dayNumber}>{meta.dayNumber}</Text>
          </View>
          <View style={styles.monthBlock}>
            <Text style={styles.monthYear}>{meta.monthYear}</Text>
            <Text style={styles.attendanceLabel}>Class attendance</Text>
            <Text style={styles.attendanceTally}>{attendanceCount} attended</Text>
            {feeLabel ? <Text style={styles.feeLabel}>{feeLabel}</Text> : null}
          </View>
        </View>

        <View style={styles.ruleHeader} />

        <View
          style={styles.tableHeader}
          {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryTableHeader: 'true' } } : {})}
        >
          <Text style={[styles.colNum, styles.headerText]}>#</Text>
          <Text style={[styles.colName, styles.headerText]}>Student</Text>
          <View style={styles.colFeeCell}>
            <Text style={[styles.headerText, styles.moneyCellText]}>Fee</Text>
          </View>
          <View style={styles.colPaidCell}>
            <Text style={[styles.headerText, styles.moneyCellText]}>Paid</Text>
          </View>
          <View style={styles.colBalanceCell}>
            <Text style={[styles.headerText, styles.moneyCellText]}>Balance</Text>
          </View>
        </View>

        <View
          style={styles.linesWrap}
          {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryLinesWrap: 'true' } } : {})}
        >
          {lines.map((line, index) => {
            const hasStudent = Boolean(line.student_name);
            const isSelected = selectedLineId === line.id;
            const signatureUrl = hasStudent
              ? resolveSignatureUrlForStudentName(signatureUrlByKey, line.student_name)
              : null;
            return (
              <View
                key={line.id ?? `line-${index}`}
                style={styles.lineWrap}
                {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryLineWrap: 'true' } } : {})}
              >
                <View style={[styles.lineRow, isSelected && styles.lineRowSelected]}>
                  <Text style={[styles.colNum, styles.lineText, !hasStudent && styles.lineTextEmpty]}>
                    {hasStudent ? `${index + 1}.` : ''}
                  </Text>
                  <View
                    style={[
                      styles.colName,
                      signatureUrl && styles.colNameSignature,
                    ]}
                    {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryColName: 'true' } } : {})}
                  >
                    {hasStudent ? (() => {
                      const canOpenPayment = !line.fee_exempt && !editorBusy;
                      if (signatureUrl) {
                        return (
                          <Pressable
                            onPress={canOpenPayment ? () => togglePaymentEditor(line) : undefined}
                            style={[
                              styles.signaturePressable,
                              isSelected && styles.signaturePressableSelected,
                            ]}
                            {...(Platform.OS === 'web'
                              ? { dataSet: { classBookDiaryStudentSignature: 'true' } }
                              : {})}
                          >
                            <Image
                              source={{ uri: signatureUrl }}
                              style={styles.signatureImage}
                              contentFit="contain"
                            />
                          </Pressable>
                        );
                      }
                      return (
                        <Text
                          style={[
                            styles.lineText,
                            !line.fee_exempt && styles.studentNamePressable,
                            isSelected && styles.studentNameSelected,
                          ]}
                          numberOfLines={1}
                          onPress={canOpenPayment ? () => togglePaymentEditor(line) : undefined}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { classBookDiaryStudentName: 'true' } }
                            : {})}
                        >
                          {line.student_name}
                        </Text>
                      );
                    })() : null}
                  </View>
                  <View
                    style={[
                      styles.colFeeCell,
                      (!hasStudent || line.fee_exempt) && styles.lineTextEmpty,
                    ]}
                  >
                    {hasStudent && !line.fee_exempt ? (
                      <Text style={[styles.moneyText, styles.moneyCellText]} numberOfLines={1}>
                        {formatClassMoney(line.fee_amount, line.fee_currency)}
                      </Text>
                    ) : null}
                  </View>
                  <View
                    style={[
                      styles.colPaidCell,
                      (!hasStudent || line.fee_exempt) && styles.lineTextEmpty,
                    ]}
                    {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryColPaid: 'true' } } : {})}
                  >
                    {hasStudent && !line.fee_exempt ? (
                      <>
                        <Text style={[styles.moneyText, styles.moneyCellText]} numberOfLines={1}>
                          {formatClassMoney(line.amount_allocated, line.fee_currency)}
                        </Text>
                        {line.extra_contributions.map((entry) => (
                          <Text
                            key={entry.id ?? `${entry.reason}-${entry.amount}`}
                            style={[styles.extraContributionText, styles.moneyCellText]}
                            {...(Platform.OS === 'web'
                              ? { dataSet: { classBookDiaryExtraContributionText: 'true' } }
                              : {})}
                          >
                            {formatExtraContributionLabel(entry)}
                          </Text>
                        ))}
                      </>
                    ) : null}
                  </View>
                  <View
                    style={[
                      styles.colBalanceCell,
                      (!hasStudent || line.fee_exempt) && styles.lineTextEmpty,
                    ]}
                  >
                    {hasStudent && !line.fee_exempt ? (
                      <Text
                        style={[
                          styles.moneyText,
                          styles.moneyCellText,
                          line.balance > 0 && styles.balancePositiveText,
                          line.balance < 0 && styles.balanceNegativeText,
                        ]}
                        numberOfLines={1}
                      >
                        {formatSignedBalance(line.balance, line.fee_currency)}
                      </Text>
                    ) : null}
                  </View>
                </View>

                {isSelected ? (
                  <View
                    style={styles.paymentEditorRow}
                    {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryFeeEditor: 'true' } } : {})}
                  >
                    <Text style={styles.paymentEditorLabel}>
                      Amount paid today ({line.fee_currency || currency})
                    </Text>
                    <View style={styles.paymentEditorControls}>
                      <TouchableOpacity
                        style={styles.stepBtn}
                        onPress={() => adjustDraftPaid(-5)}
                        disabled={editorBusy}
                        accessibilityLabel="Decrease amount by 5"
                        {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryStepBtn: 'true' } } : {})}
                      >
                        <Text style={styles.stepBtnText}>−</Text>
                      </TouchableOpacity>
                      <TextInput
                        style={styles.paymentInput}
                        value={draftPaid}
                        onChangeText={setDraftPaid}
                        keyboardType="decimal-pad"
                        editable={!editorBusy}
                        placeholder="0.00"
                        placeholderTextColor="rgba(44, 36, 22, 0.35)"
                        {...(Platform.OS === 'web'
                          ? { dataSet: { classBookDiaryFeeInput: 'true' } }
                          : {})}
                      />
                      <TouchableOpacity
                        style={styles.stepBtn}
                        onPress={() => adjustDraftPaid(5)}
                        disabled={editorBusy}
                        accessibilityLabel="Increase amount by 5"
                        {...(Platform.OS === 'web' ? { dataSet: { classBookDiaryStepBtn: 'true' } } : {})}
                      >
                        <Text style={styles.stepBtnText}>+</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.actionBtn}
                        onPress={() => void handleSavePayment(line)}
                        disabled={editorBusy}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { classBookDiaryActionBtn: 'true' } }
                          : {})}
                      >
                        {savingLinePayment ? (
                          <ActivityIndicator size="small" color="#111" />
                        ) : (
                          <Text style={styles.actionBtnText}>Save</Text>
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.actionBtn, showExtraEditor && styles.actionBtnActive]}
                        onPress={toggleExtraEditor}
                        disabled={editorBusy}
                        {...(Platform.OS === 'web'
                          ? {
                              dataSet: {
                                classBookDiaryActionBtn: 'true',
                                classBookDiaryExtraToggleBtn: 'true',
                              },
                            }
                          : {})}
                      >
                        <Text style={styles.actionBtnText}>Extra</Text>
                      </TouchableOpacity>
                    </View>
                    {paymentEditorError ? (
                      <Text style={styles.paymentEditorError}>{paymentEditorError}</Text>
                    ) : null}

                    {showExtraEditor ? (
                      <View
                        style={styles.extraEditorBlock}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { classBookDiaryExtraEditor: 'true' } }
                          : {})}
                      >
                        <Text style={styles.paymentEditorLabel}>
                          Extra ({line.fee_currency || currency})
                        </Text>
                        <View style={styles.paymentEditorControls}>
                          <TouchableOpacity
                            style={styles.stepBtn}
                            onPress={() => adjustDraftExtraPaid(-5)}
                            disabled={editorBusy}
                            accessibilityLabel="Decrease extra amount by 5"
                          >
                            <Text style={styles.stepBtnText}>−</Text>
                          </TouchableOpacity>
                          <TextInput
                            style={styles.paymentInput}
                            value={draftExtraPaid}
                            onChangeText={setDraftExtraPaid}
                            keyboardType="decimal-pad"
                            editable={!editorBusy}
                            autoFocus
                            placeholder="0.00"
                            placeholderTextColor="rgba(44, 36, 22, 0.35)"
                            {...(Platform.OS === 'web'
                              ? { dataSet: { classBookDiaryExtraInput: 'true' } }
                              : {})}
                          />
                          <TouchableOpacity
                            style={styles.stepBtn}
                            onPress={() => adjustDraftExtraPaid(5)}
                            disabled={editorBusy}
                            accessibilityLabel="Increase extra amount by 5"
                          >
                            <Text style={styles.stepBtnText}>+</Text>
                          </TouchableOpacity>
                        </View>
                        <View style={styles.editorColumnRow}>
                          <View style={styles.colNum} />
                          <View style={styles.colName} />
                          <View style={styles.colFeeCell} />
                          <View style={[styles.colPaidCell, styles.editorPaidColumn]}>
                            <Text style={[styles.reasonPickerLabel, styles.moneyCellText]}>Reason</Text>
                            <TouchableOpacity
                              style={styles.reasonTrigger}
                              onPress={toggleReasonDropdown}
                              disabled={editorBusy}
                              {...(Platform.OS === 'web'
                                ? { dataSet: { classBookDiaryExtraReasonTrigger: 'true' } }
                                : {})}
                            >
                              <Text style={[styles.reasonTriggerText, styles.moneyCellText]}>
                                {draftExtraReason}
                              </Text>
                              <Text style={styles.reasonTriggerArrow}>
                                {showReasonDropdown ? '▲' : '▼'}
                              </Text>
                            </TouchableOpacity>
                            {showReasonDropdown ? (
                              <View
                                style={styles.reasonListInline}
                                {...(Platform.OS === 'web'
                                  ? { dataSet: { classBookDiaryExtraReasonMenu: 'true' } }
                                  : {})}
                              >
                                {EXTRA_CONTRIBUTION_REASONS.map((reason, reasonIndex) => (
                                  <TouchableOpacity
                                    key={reason}
                                    style={[
                                      styles.reasonRow,
                                      reasonIndex === EXTRA_CONTRIBUTION_REASONS.length - 1 &&
                                        styles.reasonRowLast,
                                    ]}
                                    onPress={() => {
                                      setDraftExtraReason(reason);
                                      setShowReasonDropdown(false);
                                    }}
                                    {...(Platform.OS === 'web'
                                      ? { dataSet: { classBookDiaryExtraReasonItem: 'true' } }
                                      : {})}
                                  >
                                    <Text
                                      style={[
                                        styles.reasonRowText,
                                        styles.moneyCellText,
                                        reason === draftExtraReason && styles.reasonRowTextSelected,
                                      ]}
                                      {...(Platform.OS === 'web'
                                        ? { dataSet: { classBookDiaryExtraReasonText: 'true' } }
                                        : {})}
                                    >
                                      {reason}
                                    </Text>
                                  </TouchableOpacity>
                                ))}
                              </View>
                            ) : null}
                          </View>
                          <View style={styles.colBalanceCell} />
                        </View>
                        <View style={styles.editorColumnRow}>
                          <View style={styles.colNum} />
                          <View style={styles.colName} />
                          <View style={styles.colFeeCell} />
                          <View style={[styles.colPaidCell, styles.editorPaidColumn]}>
                            <View style={styles.extraSaveRow}>
                              <TouchableOpacity
                                style={styles.actionBtn}
                                onPress={() => void handleSaveExtra(line)}
                                disabled={editorBusy}
                                {...(Platform.OS === 'web'
                                  ? {
                                      dataSet: {
                                        classBookDiaryActionBtn: 'true',
                                        classBookDiaryExtraSaveBtn: 'true',
                                      },
                                    }
                                  : {})}
                              >
                                {savingLineExtra ? (
                                  <ActivityIndicator size="small" color="#111" />
                                ) : (
                                  <Text style={styles.actionBtnText}>Save</Text>
                                )}
                              </TouchableOpacity>
                            </View>
                          </View>
                          <View style={styles.colBalanceCell} />
                        </View>
                        {extraEditorError ? (
                          <Text style={styles.paymentEditorError}>{extraEditorError}</Text>
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    marginTop: 8,
    backgroundColor: 'transparent',
  },
  page: {
    position: 'relative',
    borderWidth: 1,
    borderColor: '#D8CCB8',
    borderRadius: 4,
    backgroundColor: PAPER,
    paddingHorizontal: 12,
    paddingVertical: 16,
    minHeight: 420,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 3,
    overflow: 'visible',
  },
  noClassStampWrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    backgroundColor: 'transparent',
  },
  noClassStamp: {
    transform: [{ rotate: '45deg' }],
    borderWidth: 3,
    borderColor: '#c62828',
    paddingHorizontal: 28,
    paddingVertical: 14,
    backgroundColor: 'transparent',
  },
  noClassStampText: {
    color: '#c62828',
    fontSize: 32,
    fontWeight: '800',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    backgroundColor: 'transparent',
  },
  signaturePressable: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  signaturePressableSelected: {
    opacity: 0.85,
  },
  signatureImage: {
    width: 120,
    height: 22,
    alignSelf: 'center',
    backgroundColor: 'transparent',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: 10,
    paddingHorizontal: 6,
    backgroundColor: 'transparent',
  },
  dateBlock: {
    backgroundColor: 'transparent',
  },
  weekday: {
    fontSize: 15,
    fontWeight: '600',
    color: INK,
    backgroundColor: 'transparent',
  },
  dayNumber: {
    fontSize: 42,
    lineHeight: 46,
    fontWeight: '700',
    color: INK,
    backgroundColor: 'transparent',
  },
  monthBlock: {
    alignItems: 'flex-end',
    backgroundColor: 'transparent',
  },
  monthYear: {
    fontSize: 18,
    fontWeight: '700',
    color: INK,
    backgroundColor: 'transparent',
  },
  attendanceLabel: {
    marginTop: 4,
    fontSize: 12,
    color: 'rgba(44, 36, 22, 0.65)',
    backgroundColor: 'transparent',
  },
  attendanceTally: {
    marginTop: 2,
    fontSize: 12,
    fontWeight: '700',
    color: INK,
    backgroundColor: 'transparent',
  },
  feeLabel: {
    marginTop: 2,
    fontSize: 11,
    fontWeight: '600',
    color: 'rgba(44, 36, 22, 0.75)',
    backgroundColor: 'transparent',
  },
  ruleHeader: {
    height: 2,
    backgroundColor: INK,
    marginBottom: 8,
    marginHorizontal: 6,
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 4,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
    backgroundColor: 'transparent',
  },
  headerText: {
    fontSize: 10,
    fontWeight: '700',
    color: 'rgba(44, 36, 22, 0.55)',
    textTransform: 'uppercase',
    backgroundColor: 'transparent',
  },
  linesWrap: {
    backgroundColor: PAPER,
    overflow: 'visible',
  },
  lineWrap: {
    backgroundColor: PAPER,
    overflow: 'visible',
  },
  lineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: RULE,
    minHeight: 30,
    paddingVertical: 3,
    backgroundColor: PAPER,
  },
  lineRowSelected: {
    backgroundColor: 'rgba(212, 175, 55, 0.12)',
  },
  studentNamePressable: {
    textDecorationLine: 'underline',
    textDecorationColor: 'rgba(44, 36, 22, 0.35)',
    backgroundColor: 'transparent',
  },
  studentNameSelected: {
    fontWeight: '700',
    textDecorationColor: INK,
    backgroundColor: 'transparent',
  },
  colNum: {
    width: COL_NUM_WIDTH,
    minWidth: COL_NUM_WIDTH,
    paddingTop: 1,
    fontSize: 10,
    color: 'rgba(44, 36, 22, 0.45)',
    backgroundColor: 'transparent',
  },
  colName: {
    flex: 1,
    minWidth: 0,
    paddingRight: 6,
    paddingTop: 1,
    backgroundColor: 'transparent',
  },
  colNameSignature: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  colFeeCell: {
    width: COL_FEE_WIDTH,
    minWidth: COL_FEE_WIDTH,
    flexShrink: 0,
    alignItems: 'flex-end',
    overflow: 'visible',
    marginLeft: COL_MONEY_GAP,
    backgroundColor: 'transparent',
  },
  colPaidCell: {
    width: COL_PAID_WIDTH,
    minWidth: COL_PAID_WIDTH,
    flexShrink: 0,
    alignItems: 'stretch',
    overflow: 'visible',
    marginLeft: COL_MONEY_GAP,
    backgroundColor: 'transparent',
  },
  colBalanceCell: {
    width: COL_BALANCE_WIDTH,
    minWidth: COL_BALANCE_WIDTH,
    flexShrink: 0,
    alignItems: 'flex-end',
    overflow: 'visible',
    marginLeft: COL_MONEY_GAP,
    backgroundColor: 'transparent',
  },
  moneyCellText: {
    width: '100%',
    textAlign: 'right',
    backgroundColor: 'transparent',
  },
  extraContributionText: {
    marginTop: 2,
    fontSize: 10,
    lineHeight: 13,
    color: 'rgba(44, 36, 22, 0.75)',
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  lineText: {
    fontSize: 13,
    color: INK,
    backgroundColor: 'transparent',
  },
  actionBtn: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: '#d4af37',
    minWidth: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnActive: {
    borderWidth: 1,
    borderColor: INK,
    backgroundColor: '#d4af37',
  },
  actionBtnText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#111',
    backgroundColor: 'transparent',
  },
  moneyText: {
    fontSize: 11,
    color: INK,
    backgroundColor: 'transparent',
  },
  balancePositiveText: {
    fontWeight: '700',
    color: '#8B4513',
  },
  balanceNegativeText: {
    fontWeight: '700',
    color: '#2E7D32',
  },
  lineTextEmpty: {
    color: 'transparent',
    backgroundColor: 'transparent',
  },
  paymentEditorRow: {
    paddingHorizontal: 4,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
    backgroundColor: 'rgba(250, 246, 238, 0.95)',
    overflow: 'visible',
  },
  editorColumnRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
    marginTop: 10,
    backgroundColor: 'transparent',
  },
  editorPaidColumn: {
    alignItems: 'stretch',
    overflow: 'visible',
  },
  paymentEditorLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: INK,
    marginBottom: 6,
    backgroundColor: 'transparent',
  },
  paymentEditorControls: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    backgroundColor: 'transparent',
  },
  stepBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: RULE,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
  },
  stepBtnText: {
    fontSize: 20,
    lineHeight: 22,
    fontWeight: '700',
    color: INK,
    backgroundColor: 'transparent',
  },
  paymentInput: {
    minWidth: 72,
    borderWidth: 1,
    borderColor: RULE,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontSize: 14,
    color: INK,
    textAlign: 'center',
    backgroundColor: '#fff',
  },
  paymentEditorError: {
    marginTop: 6,
    fontSize: 11,
    color: '#c62828',
    backgroundColor: 'transparent',
  },
  extraEditorBlock: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: RULE,
    backgroundColor: 'transparent',
    overflow: 'visible',
  },
  extraSaveRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    backgroundColor: 'transparent',
  },
  reasonPickerLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: INK,
    marginBottom: 6,
    backgroundColor: 'transparent',
  },
  reasonTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    minHeight: 32,
    borderWidth: 1,
    borderColor: RULE,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#fff',
  },
  reasonTriggerText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
    color: INK,
    textTransform: 'capitalize',
    backgroundColor: 'transparent',
  },
  reasonTriggerArrow: {
    marginLeft: 6,
    fontSize: 10,
    lineHeight: 16,
    color: 'rgba(44, 36, 22, 0.55)',
    backgroundColor: 'transparent',
    flexShrink: 0,
    textAlign: 'center',
  },
  reasonListInline: {
    marginTop: 4,
    alignSelf: 'stretch',
    borderWidth: 1,
    borderColor: RULE,
    borderRadius: 6,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  reasonRow: {
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
    backgroundColor: '#fff',
    alignItems: 'flex-end',
  },
  reasonRowLast: {
    borderBottomWidth: 0,
  },
  reasonRowText: {
    fontSize: 12,
    color: INK,
    textTransform: 'capitalize',
    backgroundColor: 'transparent',
  },
  reasonRowTextSelected: {
    fontWeight: '700',
  },
});
